import axios from "axios";
import { Settings, GAME_LAYOUT } from "../../../settings";
import StorageManager from "../../../utils/StorageManager";
import { PROVIDER_CONFIG } from "./providerConfig";

// Default slotface symbol IDs: l1=5, l2=6, l3=7, l4=8, l5=9 (one per reel, no winning combos)
const DEFAULT_SLOTFACE_SYMBOL_IDS = [5, 6, 7, 8, 9];

/* -------------------------------------------------------
   INLINE simulateWorker.js
------------------------------------------------------- */
const simulateWorkerCode = `
  importScripts("https://cdn.jsdelivr.net/npm/axios/dist/axios.min.js");

  onmessage = async (event) => {
    const { bet, token, apiBaseUrl, slotface, gameName } = event.data;

    try {
      const response = await axios.post(
        apiBaseUrl + "/x/simulate",
        {
          game: gameName,
          bet: bet,
          slotface: slotface
        },
        {
          headers: {
            "rs-token": token,
            "simkey": "simkey1015%"
          }
        }
      );

      postMessage({
        balance: response.data.balance,
        round: response.data.round,
        token: response.headers["rs-token"],
        bet: response.data.round[0].bet
      });

    } catch (error) {
      postMessage({ error: error.message });
    }
  };
`;

/* -------------------------------------------------------
   INLINE spinWorker.js
------------------------------------------------------- */
const spinWorkerCode = `
  importScripts("https://cdn.jsdelivr.net/npm/axios/dist/axios.min.js");

  onmessage = async (event) => {
    const { bet, token, apiBaseUrl, gameName } = event.data;

    try {
      const response = await axios.post(
        apiBaseUrl + "/x/redstone/demo/play",
        {
          game: gameName,
          bet: bet
        },
        {
          headers: {
            "rs-token": token
          }
        }
      );

      postMessage({
        token: response.headers["rs-token"],
        round: response.data.round,
        bet: response.data.bet,
        balance: response.data.balance
      });

    } catch (error) {
      // Send full error response if available (for normalization in main thread)
      if (error.response?.data) {
        postMessage({ error: error.response.data });
      } else {
        postMessage({ error: error.message });
      }
    }
  };
`;

/* -------------------------------------------------------
   WORKER FACTORY AS BLOB
------------------------------------------------------- */
function createInlineWorker(code) {
  const blob = new Blob([code], { type: "application/javascript" });
  const url = URL.createObjectURL(blob);
  return new Worker(url);
}

const simulateWorker = createInlineWorker(simulateWorkerCode);
const worker = createInlineWorker(spinWorkerCode);

/* -------------------------------------------------------
   MAIN RGS PROVIDER
------------------------------------------------------- */
class RedstoneRGS {
  constructor() {
    this.api_base_url = "https://auth-beta.redstone.rs";
    this.currentToken = null; // Track current session token
  }

  async initGame() {
    const response = await axios.post(
      `${this.api_base_url}/x/redstone/demo/init`,
      {
        game: PROVIDER_CONFIG.GAME_NAME,
        currency: PROVIDER_CONFIG.CURRENCY,
      }
    );

    // Store token for session validation
    this.currentToken = response.headers["rs-token"];
    
    // Validate session - clear storage if session changed
    this.validateSession();

    // Check for saved round
    const savedRound = StorageManager.loadBaseGameRound();

    // Redstone API doesn't provide initial reels - use default slotface when no saved round
    let round = savedRound?.round || this.generateDefaultSlotface();

    // Migration: saved rounds may have slotface, state expects reels
    if (round?.slotface && !round?.reels) {
      round = { ...round, reels: round.slotface };
      delete round.slotface;
    }

    return {
      bet: savedRound?.bet || response.data.session.bet,
      balance: response.data.session.balance, // Always use fresh balance from API
      token: response.headers["rs-token"],
      currency: response.data.session.currency?.toUpperCase(),
      game: response.data.session.game,
      bets: response.data.session.bets,
      winningLines: response.data.game.traits.lines,
      paytable: response.data.game.traits.paytable,
      round,
    };
  }

  /**
   * Adapt API spin round to state format.
   * API returns round as array with reels (each reel has buffers).
   * Trim first and last symbol from each reel to get visible symbols (4 per reel).
   * @param {Object} data - Raw worker response { round, bet, balance, ... }
   * @returns {Object|null} State-compatible round { reels, wins, win, bet } or null
   */
  adaptSpinRoundToState(data) {
    if (!data?.round?.length) return null;

    const apiRound = data.round[0];
    const rawReels = apiRound.reels;

    if (!rawReels || !Array.isArray(rawReels)) return null;

    const reels = rawReels.map((reel) =>
      Array.isArray(reel) && reel.length > 2 ? reel.slice(1, -1) : reel
    );

    return {
      reels,
      wins: apiRound.wins ?? [],
      win: apiRound.win ?? 0,
      bet: apiRound.bet ?? data.bet,
    };
  }

  /**
   * Get spin parameters - Redstone has no command/purchasedFeature, always base game
   * @param {Object} params - Universal game state (bet, featureState, purchasedFeature)
   * @returns {Object} { command, spinOptions } - command always "spin" for state compatibility
   */
  getSpinParams() {
    return { command: "spin", spinOptions: undefined };
  }

  spin(bet, token, command, purchasedFeature, spinOptions) {
    // simulate() passes slotface as 3rd arg; handleSpin passes command
    const slotface = Array.isArray(command) ? command : null;

    return new Promise((resolve, reject) => {
      const workerToUse = Settings.SIMULATION_MODE ? simulateWorker : worker;

      workerToUse.onmessage = (event) => {
        const data = event.data;
        if (data.error) {
          // Normalize error to universal format before rejecting
          const normalizedError = this.normalizeError(data.error);
          reject(normalizedError);
        } else {
          // Adapt API round to state format: extract round[0], trim reels (first+last per reel)
          const adaptedRound = this.adaptSpinRoundToState(data);
          if (adaptedRound) {
            StorageManager.saveBaseGameRound({
              round: adaptedRound,
              bet: data.bet,
              balance: data.balance,
            });
          }

          // Redstone has no features - always base game
          resolve({ 
            ...data, 
            round: adaptedRound,
            gameMode: "base",
            featureState: { type: null, phase: null, data: null }
          });
        }
      };

      workerToUse.postMessage({
        bet,
        token,
        apiBaseUrl: this.api_base_url,
        gameName: PROVIDER_CONFIG.GAME_NAME,
        ...(Settings.SIMULATION_MODE && slotface && { slotface }),
      });
    });
  }

  async simulate(slotface, bet, token) {
    return this.spin(bet, token, slotface);
  }

  transformReelsSymbols(response) {
    return response;
  }

  getResponseHandler() {
    return {
      patterns: {
        freeSpins: [
          {
            condition: (response) =>
              response.round?.length > 1 &&
              response.round[0]?.type === "feature",
            extractFreeSpins: (round) => round.slice(1),
            extractTriggerRound: (round) => round[0],
          },
          {
            condition: (response) =>
              response.round?.type === "feature" &&
              response.round?.freeSpins > 0,
            extractFreeSpins: (round) => [round],
            extractTriggerRound: (round) => round,
          },
        ],
        regular: {
          condition: (response) =>
            response.round &&
            (response.round.length === 1 ||
              response.round[0]?.type !== "feature"),
          extractRound: (round) => (round.length > 0 ? round[0] : round),
        },
      },
      propertyMap: {
        balance: "balance",
        bet: "bet",
        win: "win",
        token: "token",
        round: "round",
        type: "type",
        reels: "reels",
        wins: "wins",
        freeSpins: "freeSpins",
      },
    };
  }

  /**
   * Generate default slotface for init when Redstone API provides no initial reels.
   * Uses l1-l5 (one per reel) so no winning combination is possible at init.
   * @returns {Object} Round object with slotface, wins, win
   */
  generateDefaultSlotface() {
    const reelCount = GAME_LAYOUT.REEL_COUNT;
    const symbolsVisible = GAME_LAYOUT.SYMBOLS_VISIBLE;

    const slotface = Array.from({ length: reelCount }, (_, reelIndex) => {
      const symbolId = DEFAULT_SLOTFACE_SYMBOL_IDS[reelIndex] ?? 5;
      return Array.from({ length: symbolsVisible }, () => symbolId);
    });

    return {
      reels: slotface, // State expects reels, slotface is the source data
      wins: [],
      win: 0,
    };
  }

  /**
   * Validate session - DISABLED for Redstone
   * Redstone token changes on every init, so session validation is not applicable
   */
  validateSession() {
    // Disabled: Redstone token changes on every init
    return true;
  }

  /**
   * Get current session token
   * @returns {string|null} Current token or null
   */
  getCurrentToken() {
    return this.currentToken || null;
  }

  /**
   * Get balance management strategy for this provider
   * 
   * Redstone balance model:
   * - Server returns balance that ALREADY INCLUDES the win
   * - Client should: deduct bet immediately, then sync to server balance after animations
   * - No client-side win addition needed
   * 
   * @returns {Object} Balance strategy configuration
   */
  getBalanceStrategy() {
    return {
      // Server balance already includes the current round's win
      serverIncludesWin: true,
      
      // Client should deduct bet immediately on successful response
      clientDeductsBet: true,
      
      // Client should NOT add win (server already did)
      clientAddsWin: false,
    };
  }

  /**
   * Normalize error response to universal format
   * 
   * Redstone error format:
   * {
   *   "error": {
   *     "code": "INSUFFICIENT_BALANCE",
   *     "message": "Insufficient balance"
   *   }
   * }
   * 
   * Universal format (PopiPlay-based):
   * {
   *   "errors": [{
   *     "code": 301,
   *     "desc": "not_enough_money"
   *   }]
   * }
   * 
   * @param {Object} error - Provider-specific error object
   * @returns {Object} Normalized error in universal format
   */
  normalizeError(error) {
    // If error is already in universal format, return as-is
    if (error?.errors && Array.isArray(error.errors)) {
      return error;
    }

    // Handle Redstone error format: { error: { code: "STRING", message: "..." } }
    if (error?.error?.code) {
      const providerCode = error.error.code;
      const universalCode = PROVIDER_CONFIG.ERROR_CODES[providerCode] || 999; // 999 = unknown error
      
      const normalized = {
        errors: [{
          code: universalCode,
          desc: error.error.message || providerCode.toLowerCase()
        }]
      };
      
      return normalized;
    }

    // Handle string errors
    if (typeof error === 'string') {
      return {
        errors: [{
          code: 999,
          desc: error
        }]
      };
    }

    // Unknown format - return generic error
    return {
      errors: [{
        code: 999,
        desc: "Unknown error"
      }]
    };
  }

  /**
   * Determine if balance should be synced with server when win animation closes
   * 
   * Redstone: NO - Server init endpoint returns session start balance, not current balance
   * Balance is already correct in Redux from spin response (client-side tracking)
   * 
   * @returns {boolean} False - skip balance sync on win close
   */
  shouldSyncBalanceOnWinClose() {
    return false;
  }
}

export default RedstoneRGS;
